import {
  MAX_CODE_STEP_SAVE_ERRORS,
  MAX_CODE_STEP_SAVE_MESSAGE_CHARS,
  MAX_CODE_STEP_SAVE_RESULT_BYTES,
  type CodeStepSaveResult,
  type ResultUpload,
} from 'ts-components/PiChat/protocol';
import {
  defaultWarn,
  presignedPut,
  presignedPutBeforeExpiry,
  resolveUploadUrls,
  type PutStatus,
} from 'ts-components/PiChat/presignedPut';

export interface ResolvedResultUpload {
  url: string;
  headers: Record<string, string>;
  maxBytes: number;
  expiresAt: number;
}

export const resolveResultUpload = (
  { result, expiresAt }: ResultUpload,
  wsUrl: string
): ResolvedResultUpload | null => {
  const urls = resolveUploadUrls(
    [result.url],
    wsUrl,
    'code_step.save',
    defaultWarn
  );
  if (!urls) return null;
  const [url] = urls;
  return {
    url,
    headers: result.headers,
    maxBytes: Math.min(result.maxBytes, MAX_CODE_STEP_SAVE_RESULT_BYTES),
    expiresAt,
  };
};

const clip = (text: string) => text.slice(0, MAX_CODE_STEP_SAVE_MESSAGE_CHARS);

export const encodeResult = (
  result: CodeStepSaveResult,
  maxBytes: number
): string => {
  if (result.ok) return JSON.stringify(result);
  const { code } = result;
  const message =
    result.message === undefined ? undefined : clip(result.message);
  const errors = result.errors?.slice(0, MAX_CODE_STEP_SAVE_ERRORS).map(clip);
  const bodies = [
    { ok: false, code, message, errors },
    { ok: false, code, message },
    { ok: false, code },
  ].map((body) => JSON.stringify(body));
  const encoder = new TextEncoder();
  return (
    bodies.find((body) => encoder.encode(body).length <= maxBytes) ??
    bodies[bodies.length - 1]
  );
};

export const sendCodeStepSaveResult = (
  upload: ResolvedResultUpload,
  result: CodeStepSaveResult
): Promise<PutStatus> =>
  presignedPutBeforeExpiry(
    upload.expiresAt,
    'code_step.save not answered: its upload URL has expired',
    defaultWarn,
    (signal) =>
      presignedPut(
        upload,
        new Blob([encodeResult(result, upload.maxBytes)]),
        'code_step.save result',
        signal,
        (...args) => globalThis.fetch(...args),
        defaultWarn
      )
  );
